// cookieOnlySignalRService.js - No token access needed
import * as signalR from '@microsoft/signalr';
import {BASE_URL } from '../config/axiosConfig';

class CookieOnlySignalRService {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.connectionPromise = null;
    this.alertCallbacks = new Set();
  }

  async startConnection() {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = this._initializeConnection();
    return this.connectionPromise;
  }

  async _initializeConnection() {
    try {
      console.log('🔗 Initializing SignalR connection with cookies...');
      const signalRUrl = `${BASE_URL}/hubs/alert`;
      // Create connection - NO TOKEN NEEDED! Uses HTTP-only cookies automatically
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(signalRUrl, {
          withCredentials: true, // This sends HTTP-only cookies
          transport: signalR.HttpTransportType.WebSockets,
          skipNegotiation: false,
          // NO accessTokenFactory needed!
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            console.log(`🔄 SignalR reconnect attempt ${retryContext.previousRetryCount + 1}`);
            if (retryContext.previousRetryCount < 3) {
              return 2000;
            } else if (retryContext.previousRetryCount < 7) {
              return 5000;
            } else {
              return 10000;
            }
          }
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Set up event handlers
      this._setupEventHandlers();

      // Start connection
      await this.connection.start();
      
      this.isConnected = true;
      this.reconnectAttempts = 0;
      console.log('✅ SignalR Connected successfully using cookies');

      return this.connection;
    } catch (error) {
      console.error('❌ SignalR Connection failed:', error);
      this.isConnected = false;
      this.connectionPromise = null;
      throw error;
    }
  }

  _setupEventHandlers() {
    if (!this.connection) return;

    // Handle incoming alerts
    this.connection.on('ReceiveAlert', (alert) => {
      console.log('📢 Received SignalR Alert:', alert);
      
      // Notify all registered callbacks
      this.alertCallbacks.forEach(callback => {
        try {
          callback(alert);
        } catch (error) {
          console.error('Error in alert callback:', error);
        }
      });
    });

    // Handle connection confirmation (if your enhanced hub sends it)
    this.connection.on('Connected', (data) => {
      console.log('✅ SignalR connection confirmed:', data);
    });

    // Handle ping responses
    this.connection.on('ReceivePing', (data) => {
      console.log('🏓 Ping response:', data);
    });

    // Connection state handlers
    this.connection.onclose((error) => {
      console.log('❌ SignalR Connection closed:', error);
      this.isConnected = false;
      this.connectionPromise = null;
    });

    this.connection.onreconnecting((error) => {
      console.log('🔄 SignalR Reconnecting:', error);
      this.isConnected = false;
    });

    this.connection.onreconnected((connectionId) => {
      console.log('✅ SignalR Reconnected with ID:', connectionId);
      this.isConnected = true;
      this.reconnectAttempts = 0;
    });
  }

  // Send ping to test connection
  async sendPing() {
    if (!this.isConnected || !this.connection) {
      throw new Error('SignalR connection not established');
    }
    
    try {
      await this.connection.invoke('SendPing');
    } catch (error) {
      console.error('Failed to send ping:', error);
      throw error;
    }
  }

  // Subscribe to alerts
  subscribeToAlerts(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    this.alertCallbacks.add(callback);
    console.log(`📝 Alert callback registered. Total callbacks: ${this.alertCallbacks.size}`);
    
    return () => {
      this.alertCallbacks.delete(callback);
      console.log(`🗑️ Alert callback removed. Remaining: ${this.alertCallbacks.size}`);
    };
  }

  // Stop connection
  async stopConnection() {
    if (this.connection) {
      try {
        console.log('🔌 Stopping SignalR connection...');
        await this.connection.stop();
        console.log('✅ SignalR Connection stopped');
      } catch (error) {
        console.error('❌ Error stopping SignalR:', error);
      } finally {
        this.isConnected = false;
        this.connection = null;
        this.connectionPromise = null;
        this.alertCallbacks.clear();
      }
    }
  }

  // Get connection state
  getConnectionState() {
    if (!this.connection) {
      return 'Disconnected';
    }
    
    switch (this.connection.state) {
      case signalR.HubConnectionState.Connecting:
        return 'Connecting';
      case signalR.HubConnectionState.Connected:
        return 'Connected';
      case signalR.HubConnectionState.Disconnecting:
        return 'Disconnecting';
      case signalR.HubConnectionState.Disconnected:
        return 'Disconnected';
      default:
        return 'Unknown';
    }
  }

  // Check if connected
  get connected() {
    return this.isConnected && 
           this.connection?.state === signalR.HubConnectionState.Connected;
  }

  // Get simple connection stats
  getConnectionStats() {
    return {
      isConnected: this.isConnected,
      state: this.getConnectionState(),
      reconnectAttempts: this.reconnectAttempts,
      activeCallbacks: this.alertCallbacks.size
    };
  }
}

// Create and export singleton instance
const cookieOnlySignalRService = new CookieOnlySignalRService();
export default cookieOnlySignalRService;